import { PlusIcon } from "@heroicons/react/24/outline";
import React, { PropsWithChildren } from "react";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";
import { GoalCardItem } from "@fronterahealth/frontera-ui-components/GoalCarousel/GoalCard";
import { GoalCarouselProps } from "@fronterahealth/frontera-ui-components/GoalCarousel/GoalCarousel";

import { ApiTreatmentPlanTemplateAiGenerationConfigChoices, ReportGoalTypeEdge } from "@api/graphql/types-and-hooks";
import { DomainDescription } from "@components/DomainDescription/DomainDescription";
import { AddShortTermGoalsBank } from "@components/GoalsBank/AddShortTermGoalsBank/AddShortTermGoalsBankV2";

interface ShortTermGoalParentCardProps extends PropsWithChildren {
  title: string;
  aiGoals: GoalCardItem[];
  aiGoalList?: ReportGoalTypeEdge[];
  reportId?: string;
  reportDomainId?: string;
  learnerId?: string;
  refetch?: () => void;
  aiGoalsBeingUsed: string[];
  addAiGoals: GoalCarouselProps["onAdd"];
  subtitle?: string;
  onButtonClick: () => void;
  onAiGoalUpDownVote: (goal: GoalCardItem, vote: boolean) => Promise<void>;
  uiGoalId?: string;
  isGoalBankSuggestionsEnabled?: boolean;
  aiGenerationConfig?: ApiTreatmentPlanTemplateAiGenerationConfigChoices;
}

export const ShortTermGoalParentCard: React.FC<ShortTermGoalParentCardProps> = ({
  children,
  onButtonClick,
  title,
  aiGoalList,
  reportId,
  learnerId,
  reportDomainId,
  refetch,
  subtitle,
  uiGoalId,
  isGoalBankSuggestionsEnabled,
  aiGenerationConfig,
}) => {
  return (
    <div
      data-testid={`short-term-goal-parent-card-${title}`}
      className="h-full border border-border-strong rounded-2xl p-4 bg-white my-4 "
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-start flex-1 min-w-0">
          {uiGoalId && (
            <div className="w-6 shrink-0">
              <Paragraph displayType="loud" colorType="secondary">
                {uiGoalId}
              </Paragraph>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <Paragraph displayType="loud" colorType="primary">
              {title}
            </Paragraph>
            {/* Subtitle is the domain description (markdown) — render as rich text. */}
            {subtitle && <DomainDescription content={subtitle} />}
          </div>
        </div>
        <div className="flex flex-row gap-2 justify-items-center items-center">
          <AddShortTermGoalsBank
            learnerId={learnerId ?? "<missing-learner-id"}
            reportDomainId={reportDomainId ?? "<missing-report-domain-id>"}
            reportId={reportId ?? "<missing-report-id>"}
            onAdd={async () => {
              if (refetch) await refetch();
            }}
            aiGoals={aiGoalList ?? []}
            isGoalBankSuggestionsEnabled={isGoalBankSuggestionsEnabled}
            aiGenerationConfig={aiGenerationConfig}
          />

          <Button
            onClick={() => onButtonClick()}
            variant="secondary"
            buttonStyle="border"
            size="md"
            text={
              <span className="flex items-center gap-1.5">
                <PlusIcon className="h-4 w-4" />
                Custom Goal
              </span>
            }
          />
        </div>
      </div>

      {children}
    </div>
  );
};
